import React from 'react'
import makeStyles from '@mui/styles/makeStyles'
import {
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    DialogTitle,
    DialogContent,
    DialogActions,
    useTheme,
} from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import clsx from 'clsx'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import styles from './_styles/dialogStyles'
import useButtonStyles from '../../styles/buttonsStyles'
import { TokTokDocDialogButtonsOptions, TokTokDocDialogProps } from './_props/TokTokDocDialogProps'
import { defaultPalette } from '../../theme/idsTheme'
import { hexToRgba } from '../../utils/commonUtils'

// @ts-expect-error TS(2345) FIXME: Argument of type '(theme: any) => { dialogPaper: {... Remove this comment to see the full error message
const useStyles = makeStyles(styles)

/**
 * TokTokDocDialog custom of material ui Dialog
 *
 * @param {boolean} isOpen dialog is open or not
 * @param {Function} handleClose callback called when CANCEL button is clicked
 * @param {Function} handleSave callback called when confirmed/delete button is clicked | if null then no confirmed/delete button
 * @param {string} title Title of the dialog
 * @param {string} subtitle Subtitle of the dialog
 * @param {Component} children Content of the dialog
 * @param {string} saveTitle text of the save action
 * @param {boolean} isDeleteAction if true then button style delete if false button style confirmed
 * @param {boolean} loading loader for action
 * @param {boolean} disableEscape prevent user to close dialog without click on cancel
 * @param {Object} classNames material classes
 * @param {boolean} isHelpCenter change width if dialog is used for help center
 */

export default function TokTokDocDialog({
    isOpen,
    handleClose,
    handleSave,
    handleCancel,
    title,
    secondTitle,
    subtitle,
    children,
    saveTitle,
    cancelTitle,
    isDeleteAction,
    loading,
    disableEscape,
    disableCondition = false,
    classNames,
    saveButtonCy,
    sx,
    maxWidth,
    buttonsOptions,
    saveStartIcon,
    dialogProps,
}: TokTokDocDialogProps) {
    const theme = useTheme()
    const classes = useStyles()
    const buttonsClasses = useButtonStyles()
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
    const hasSaveAction = !!handleSave
    const hasCancelAction = cancelTitle !== null

    const computedButtonsOptions: TokTokDocDialogButtonsOptions = {
        colors: {
            save: buttonsOptions?.colors?.save ?? defaultPalette.primary.main,
            cancel: buttonsOptions?.colors?.cancel ?? defaultPalette.lightGrey.mediumDark,
        },
    }

    const saveColor = isDeleteAction ? defaultPalette.red.main : computedButtonsOptions.colors.save

    const computeSaveButtonStartIcon = () => {
        if (isDeleteAction) return <DeleteIcon />
        return saveStartIcon ?? null
    }

    return (
        <Dialog
            fullScreen={isSmall}
            open={isOpen}
            onClose={handleClose}
            maxWidth={false}
            aria-labelledby="dialog-title"
            classes={{
                ...classNames,
                paper: clsx(classes.dialogPaper, classNames && classNames.paper && classNames.paper),
            }}
            sx={{
                ...sx,
                '& .MuiDialog-paper': {
                    maxWidth,
                },
            }}
            disableEscapeKeyDown={disableEscape}
            {...dialogProps}
        >
            <div data-cy="ttdDialog" className={classes.dialogContentContainer}>
                <div className={classes.dialogContentContainerTop}>
                    <div className={classes.dialogContainerTitle}>
                        <div className={classes.dialogTitleContainer}>
                            <DialogTitle id="dialog-title">
                                <span className={classes.dialogTitle}>{title}</span>
                            </DialogTitle>
                            <DialogTitle id="dialog-title">
                                <span className={classes.dialogTitleColored}>{secondTitle}</span>
                            </DialogTitle>
                            <DialogTitle className={classes.dialogSubTitle}>
                                <span>{subtitle}</span>
                            </DialogTitle>
                        </div>
                        {false && (
                            <div className={classes.dialogCloseContainer}>
                                <IconButton onClick={handleClose} className={classes.dialogCloseIcon}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        )}
                    </div>
                </div>
                <DialogContent className={classes.dialogContent}>{children}</DialogContent>
                <DialogActions className={classes.dialogActions}>
                    {hasCancelAction && (
                        <Button
                            disableElevation
                            className={clsx(classes.button, buttonsClasses.ButtonCancel)}
                            onClick={handleCancel ?? handleClose}
                            data-cy="dialogCancelAction"
                            sx={{
                                background: `${computedButtonsOptions.colors.cancel} !important`,
                                '&:hover': {
                                    background: `${hexToRgba(
                                        computedButtonsOptions.colors.cancel ?? '',
                                        0.7,
                                    )} !important`,
                                },
                            }}
                        >
                            {cancelTitle ?? 'Annuler'}
                        </Button>
                    )}
                    {hasSaveAction && (
                        <Button
                            disableElevation
                            disabled={loading || disableCondition}
                            className={clsx(classes.button, isDeleteAction ? classes.deleteButton : '')}
                            variant="contained"
                            onClick={handleSave}
                            data-cy={saveButtonCy ?? 'delete'}
                            sx={{
                                background: `${disableCondition ? '' : saveColor} !important`,
                                '&:hover': {
                                    background: `${hexToRgba(saveColor ?? '', 0.7)} !important`,
                                },
                            }}
                            startIcon={computeSaveButtonStartIcon()}
                        >
                            {loading ? <CircularProgress size={24} className={classes.buttonProgress} /> : saveTitle}
                        </Button>
                    )}
                </DialogActions>
            </div>
        </Dialog>
    )
}
